import type { MetricWrite } from '../../api/dispatchMetrics';
import type { DispatchMetric } from '../../api/types';
import { KPI_PRESETS } from './metricMeta';

/**
 * Ready-made metric sets an operator can opt into, instead of a blank form on
 * an empty dashboard.
 *
 * The blank form asks a newcomer four questions they have no way to answer yet
 * — which category, which of twenty-six subcategories, bar or line, and what a
 * KPI header is — and the honest answer to all four is "whatever everybody else
 * has". So the packs are that answer, written down. Custom is still there and
 * unchanged; this is a starting point, not a replacement for it.
 *
 * Three rules hold the list together:
 *
 * - **Curated means good, not exhaustive.** A card that mixes a count with an
 *   amount is a legal metric and a bad one — one axis cannot be both — so it is
 *   not offered here even though the screen can draw it. The pathological
 *   shapes live in the preview fixtures, which is where they belong.
 * - **A pack is a reading, not a category dump.** Each one answers a question
 *   somebody actually asks ("how did today go", "who owes what", "what is on
 *   the board right now"), which is why a pack mixes categories and why the
 *   same subcategory can appear in two of them.
 * - **Every preset is a metric the other dashboard can draw**, because it is
 *   written into dispatch and the Flutter page reads the same rows. So the
 *   `groupBy`/`arrangeBy` pairs here are the ones that page offers, not free
 *   text — `metricForm.ts` says which arrangement each grouping allows.
 *
 * Priorities descend within a pack so an added pack reads top to bottom as
 * written: KPIs first, then the charts under them. They are deliberately ABOVE
 * the 0 a custom create lands at, so opting into a pack puts it at the top
 * rather than scattering it through whatever is already there.
 *
 * **Every pack carries exactly FIVE KPIs under one header.** A KPI row is a
 * header group (`groupMetrics`), so a pack taken whole is one row rather than
 * two cards here and three there. Five is the pack SIZE, not a promise about
 * the width: the grid fits as many 215px cards as the viewport allows, so five
 * fills a row around 1150px and leaves air on a wider screen — which is the
 * trade that keeps a KPI card the size of a KPI card rather than stretching one
 * number across a third of a monitor. Two of those headers are dispatch's own
 * presets rather than words invented here: the essentials use `KPI Row`, the
 * headline band that prints no heading, and the board uses `Dispatch Row`,
 * which the Flutter page labels "Dispatch". A row mixes an all-time figure in
 * with windowed ones where that is what makes five — the card itself is what
 * says which, by carrying no change against a previous window.
 */

export interface Preset {
  /** Stable within a pack; the checkbox key and what the caller reports on. */
  id: string;
  /** One line on what the card answers — the reason to tick it. */
  blurb: string;
  write: MetricWrite;
}

export interface PresetPack {
  id: string;
  label: string;
  blurb: string;
  presets: Preset[];
}

const kpi = (
  id: string,
  blurb: string,
  over: Partial<MetricWrite> & Pick<MetricWrite, 'category' | 'subCategory' | 'metricsName'>,
): Preset => ({
  id,
  blurb,
  write: { groupBy: 'day', arrangeBy: '', type: 'kpi', priority: 0, ...over },
});

const chart = (
  id: string,
  blurb: string,
  over: Partial<MetricWrite> & Pick<MetricWrite, 'category' | 'subCategory' | 'metricsName'>,
): Preset => ({
  id,
  blurb,
  write: { groupBy: 'day', arrangeBy: 'daily_over_month', type: 'barChart', priority: 0, ...over },
});

export const PRESET_PACKS: PresetPack[] = [
  {
    id: 'starter',
    label: 'The essentials',
    blurb: 'The headline row most operators start from — the day’s work and what it was worth.',
    presets: [
      kpi('new-jobs', 'How many jobs were booked in the window.', {
        category: 'jobs',
        subCategory: ['NEW_JOBS'],
        metricsName: 'New jobs',
        arrangeBy: KPI_PRESETS.kpiRow,
        priority: 200,
      }),
      kpi('done-jobs', 'Jobs completed.', {
        category: 'jobs',
        subCategory: ['DONE_JOBS'],
        metricsName: 'Done jobs',
        arrangeBy: KPI_PRESETS.kpiRow,
        priority: 195,
      }),
      kpi('cancelled-jobs', 'Jobs cancelled.', {
        category: 'jobs',
        subCategory: ['CANCELLED_JOBS'],
        metricsName: 'Cancelled jobs',
        arrangeBy: KPI_PRESETS.kpiRow,
        priority: 190,
      }),
      kpi('pending-jobs', 'Jobs still open — all time, so the window does not narrow it.', {
        category: 'jobs',
        subCategory: ['PENDING_JOBS'],
        metricsName: 'Pending jobs',
        groupBy: 'all',
        arrangeBy: KPI_PRESETS.kpiRow,
        priority: 185,
      }),
      kpi('job-revenue', 'What those jobs are worth.', {
        category: 'jobs',
        subCategory: ['JOBS_TOTAL'],
        metricsName: 'Job revenue',
        arrangeBy: KPI_PRESETS.kpiRow,
        priority: 180,
      }),
      chart('jobs-outcome', 'Done against cancelled, no-show and rejected, day by day.', {
        category: 'jobs',
        subCategory: ['DONE_JOBS', 'CANCELLED_JOBS', 'NO_SHOW_JOBS', 'REJECTED_JOBS'],
        metricsName: 'Jobs by outcome',
        priority: 175,
      }),
      chart('revenue-trend', 'Job revenue as a monthly trend line.', {
        category: 'jobs',
        subCategory: ['JOBS_TOTAL'],
        metricsName: 'Job revenue trend',
        type: 'lineChart',
        groupBy: 'month',
        arrangeBy: 'month',
        priority: 170,
      }),
    ],
  },
  {
    id: 'board',
    label: 'On the board right now',
    blurb:
      'Counted from the job board rather than the metrics API — these answer for the jobs in the window as they stand. Dispatch metrics are KPIs, so this pack is one row.',
    presets: [
      kpi('payment-due', 'Jobs on the board with a balance outstanding.', {
        category: 'dispatch',
        subCategory: ['DISPATCH_PAYMENT_DUE'],
        metricsName: 'Payment due',
        arrangeBy: KPI_PRESETS.dispatchRow,
        priority: 165,
      }),
      kpi('needs-attention', 'Jobs flagged for attention or running late.', {
        category: 'dispatch',
        subCategory: ['DISPATCH_NEED_ATTENTION', 'DISPATCH_DELAYED'],
        metricsName: 'Needs attention',
        arrangeBy: KPI_PRESETS.dispatchRow,
        priority: 160,
      }),
      kpi('awaiting-driver', 'Jobs with nobody assigned yet, in house or farmed out.', {
        category: 'dispatch',
        subCategory: ['DISPATCH_AWAITING_DRIVER', 'DISPATCH_FARM_OUT_UNASSIGNED'],
        metricsName: 'Awaiting a chauffeur',
        arrangeBy: KPI_PRESETS.dispatchRow,
        priority: 155,
      }),
      kpi('farmed-out', 'Work sent to somebody else.', {
        category: 'dispatch',
        subCategory: ['DISPATCH_FARM_OUT'],
        metricsName: 'Farmed out',
        arrangeBy: KPI_PRESETS.dispatchRow,
        priority: 150,
      }),
      kpi('in-progress', 'Jobs running now.', {
        category: 'dispatch',
        subCategory: ['DISPATCH_IN_PROGRESS'],
        metricsName: 'In progress',
        arrangeBy: KPI_PRESETS.dispatchRow,
        priority: 145,
      }),
    ],
  },
  {
    id: 'money',
    label: 'Money in and out',
    blurb: 'Invoicing, what is still owed either way, and what has gone out to chauffeurs and affiliates.',
    presets: [
      kpi('invoice-received', 'Payments received against invoices.', {
        category: 'invoices',
        subCategory: ['INVOICE_RECEIVED'],
        metricsName: 'Invoice received',
        arrangeBy: 'Money',
        priority: 140,
      }),
      kpi('invoice-due', 'Invoiced and not yet paid.', {
        category: 'invoices',
        subCategory: ['INVOICE_DUE'],
        metricsName: 'Invoice due',
        arrangeBy: 'Money',
        priority: 135,
      }),
      kpi('invoice-total', 'Everything invoiced in the window.', {
        category: 'invoices',
        subCategory: ['INVOICE_TOTAL'],
        metricsName: 'Invoice total',
        arrangeBy: 'Money',
        priority: 130,
      }),
      kpi('payables-total', 'What is owed to chauffeurs and affiliates, all time.', {
        category: 'payables',
        subCategory: ['PAYABLES_TOTAL'],
        metricsName: 'Outstanding payables',
        groupBy: 'all',
        arrangeBy: 'Money',
        priority: 125,
      }),
      kpi('paid-out', 'What has actually been paid out.', {
        category: 'paylog',
        subCategory: ['PAYLOG_TOTAL'],
        metricsName: 'Paid out',
        arrangeBy: 'Money',
        priority: 120,
      }),
      chart('invoice-flow', 'Invoices raised, finalized and closed, week by week.', {
        category: 'invoices',
        subCategory: ['NEW_INVOICES', 'INVOICE_FINALIZED', 'CLOSED_INVOICES'],
        metricsName: 'Invoice flow',
        type: 'lineChart',
        groupBy: 'week',
        arrangeBy: 'month_weeks',
        priority: 115,
      }),
      chart('payables-payee', 'What is owed, split between chauffeurs and affiliates.', {
        category: 'payables',
        subCategory: ['PAYABLES_CHAUFFEUR', 'PAYABLES_AFFILIATE'],
        metricsName: 'Payables by payee',
        priority: 110,
      }),
      chart('paid-out-trend', 'Payments out to chauffeurs against affiliates.', {
        category: 'paylog',
        subCategory: ['PAYLOG_CHAUFFEUR', 'PAYLOG_AFFILIATE'],
        metricsName: 'Paid out trend',
        type: 'lineChart',
        priority: 105,
      }),
    ],
  },
  {
    id: 'quotes',
    label: 'Quotes and conversion',
    blurb: 'What was quoted, what came back, and what it was worth.',
    presets: [
      kpi('new-quotes', 'Quotes raised in the window.', {
        category: 'quotes',
        subCategory: ['NEW_QUOTES'],
        metricsName: 'New quotes',
        arrangeBy: 'Quotes',
        priority: 100,
      }),
      kpi('accepted-quotes', 'Quotes the customer accepted.', {
        category: 'quotes',
        subCategory: ['ACCEPTED_QUOTES'],
        metricsName: 'Accepted quotes',
        arrangeBy: 'Quotes',
        priority: 95,
      }),
      kpi('declined-quotes', 'Quotes the customer turned down.', {
        category: 'quotes',
        subCategory: ['DECLINED_QUOTES'],
        metricsName: 'Declined quotes',
        arrangeBy: 'Quotes',
        priority: 90,
      }),
      kpi('pending-quotes', 'Quotes still waiting on an answer, all time.', {
        category: 'quotes',
        subCategory: ['PENDING_QUOTES'],
        metricsName: 'Pending quotes',
        groupBy: 'all',
        arrangeBy: 'Quotes',
        priority: 85,
      }),
      kpi('quote-value', 'What was quoted, in money.', {
        category: 'quotes',
        subCategory: ['QUOTES_TOTAL'],
        metricsName: 'Quote value',
        arrangeBy: 'Quotes',
        priority: 80,
      }),
      chart('quote-outcome', 'Accepted against declined, day by day.', {
        category: 'quotes',
        subCategory: ['ACCEPTED_QUOTES', 'DECLINED_QUOTES'],
        metricsName: 'Quote outcomes',
        priority: 75,
      }),
      chart('quote-won', 'Everything quoted against what was won.', {
        category: 'quotes',
        subCategory: ['QUOTES_TOTAL', 'QUOTES_ACCEPTED_TOTAL'],
        metricsName: 'Quoted against won',
        priority: 70,
      }),
    ],
  },
  {
    id: 'people',
    label: 'Customers and chauffeurs',
    blurb: 'Who is joining — new accounts, new affiliates, and the chauffeur pipeline.',
    presets: [
      kpi('new-customers', 'Customer accounts created in the window.', {
        category: 'customers',
        subCategory: ['NEW_CUSTOMER'],
        metricsName: 'New customers',
        arrangeBy: 'People',
        priority: 65,
      }),
      kpi('new-affiliates', 'Affiliates added in the window.', {
        category: 'affiliates',
        subCategory: ['NEW_AFFILIATE'],
        metricsName: 'New affiliates',
        arrangeBy: 'People',
        priority: 60,
      }),
      // One word on purpose: "Chauffeur applications" wraps to two lines in a
      // KPI card, and its subcategory line wraps too — which puts the figure a
      // line below the four beside it. The subtitle still says what it counts.
      kpi('driver-applications', 'Chauffeur application forms received.', {
        category: 'drivers',
        subCategory: ['DRIVER_FORM_SUBMISSIONS'],
        metricsName: 'Applications',
        arrangeBy: 'People',
        priority: 55,
      }),
      kpi('approved-drivers', 'Chauffeurs approved in the window.', {
        category: 'drivers',
        subCategory: ['APPROVED_DRIVERS'],
        metricsName: 'Approved chauffeurs',
        arrangeBy: 'People',
        priority: 50,
      }),
      kpi('pending-drivers', 'Chauffeurs waiting on approval, all time.', {
        category: 'drivers',
        subCategory: ['PENDING_DRIVERS'],
        metricsName: 'Pending chauffeurs',
        groupBy: 'all',
        arrangeBy: 'People',
        priority: 45,
      }),
      chart('driver-pipeline', 'Applications received against chauffeurs approved.', {
        category: 'drivers',
        subCategory: ['DRIVER_FORM_SUBMISSIONS', 'APPROVED_DRIVERS'],
        metricsName: 'Chauffeur pipeline',
        type: 'lineChart',
        priority: 40,
      }),
    ],
  },
];

/* ---- what is already there ------------------------------------------------ */

const sameCodes = (a: string[], b: string[]): boolean =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

/**
 * Whether the dashboard already carries this preset.
 *
 * Matched on the SHAPE, not the name: the tenant may well have a card of their
 * own called "New jobs", and offering the preset as already-added would hide a
 * metric they never got. The name is compared too, so re-adding a pack whose
 * card somebody renamed offers it again rather than pretending it is there —
 * the safer way round, since the worst case is a duplicate somebody deletes and
 * the other is a card they asked for and never received.
 */
export const isPresent = (preset: Preset, metrics: DispatchMetric[]): boolean =>
  metrics.some(
    (metric) =>
      metric.metricsName === preset.write.metricsName &&
      String(metric.category || '').toLowerCase() === preset.write.category &&
      String(metric.type || '') === preset.write.type &&
      sameCodes(metric.subCategory || [], preset.write.subCategory),
  );

export interface PackStatus {
  total: number;
  present: number;
  missing: Preset[];
}

export const packStatus = (pack: PresetPack, metrics: DispatchMetric[]): PackStatus => {
  const missing = pack.presets.filter((preset) => !isPresent(preset, metrics));
  return { total: pack.presets.length, present: pack.presets.length - missing.length, missing };
};

/** Every preset across every pack, for the "add everything" case. */
export const allPresets = (): Preset[] => PRESET_PACKS.flatMap((pack) => pack.presets);

/**
 * The presets a selection names, in CATALOG order rather than click order.
 *
 * They are written one at a time and priority decides the layout, so sending
 * them as the catalog lists them means a half-failed run still reads top to
 * bottom instead of in whatever order the boxes happened to be ticked.
 */
export const selected = (ids: Set<string>): Preset[] =>
  allPresets().filter((preset) => ids.has(preset.id));

/**
 * What arrives ticked when the dialog opens.
 *
 * The essentials, but only on a dashboard with nothing on it — that is the one
 * moment a ready-made set is worth more than a form. Where somebody has already
 * built one, a tab that pre-selects six cards is one stray click from six they
 * have to go and delete.
 */
export const startingPicks = (metrics: DispatchMetric[]): Set<string> =>
  metrics.length ? new Set<string>() : new Set(PRESET_PACKS[0].presets.map((preset) => preset.id));

/* ---- adding them ---------------------------------------------------------- */

export interface AddOutcome {
  /** How many were created before the run stopped, or all of them. */
  done: number;
  /** What to tell the operator when it stopped early; null when every one landed. */
  error: string | null;
}

/**
 * Create the chosen presets ONE AT A TIME, stopping at the first refusal.
 *
 * Never `Promise.all`: this is somebody else's service, and a burst of two
 * dozen creates on the strength of one click is how opting into a pack becomes
 * a rate-limit incident — the same reason the reorder PATCHes sequentially.
 *
 * A failure part way does NOT roll back, because there is nothing to roll back
 * to: every create before it already exists in dispatch and on the Flutter
 * page. So the run says how far it got, the caller re-reads the list, and the
 * dialog shows what landed as `already added` — pressing Add again resumes
 * rather than duplicating.
 */
export const addInOrder = async (
  presets: Preset[],
  create: (write: Preset['write']) => Promise<unknown>,
  onProgress: (done: number) => void = () => {},
): Promise<AddOutcome> => {
  let done = 0;
  for (const preset of presets) {
    try {
      await create(preset.write);
    } catch (error) {
      const why = error instanceof Error ? error.message : 'Could not add those metrics.';
      return { done, error: done ? `Added ${done} of ${presets.length}, then: ${why}` : why };
    }
    done += 1;
    onProgress(done);
  }
  return { done, error: null };
};
